import React, { useState, useEffect, useRef } from 'react';
import { Lock, Shield, AlertCircle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { setDataToken } from '../lib/supabase';
import { bangunkanAI } from '../lib/hfWake';
import TurnstileWidget, { turnstileAktif } from './TurnstileWidget';

// Kunci akses untuk halaman sensitif — 2-tier:
//   level='profiling' → HORUS_PROFILING_USER/KEY (Subject Profiling intel)
//   level='datacenter'→ HORUS_DATACENTER_USER/KEY (Admin DB management)
// Auth check via server (/api/auth-check) — kredensial NEVER di client bundle.
// Sesi: simpan signed JWT token per-tier di sessionStorage (4 jam, lalu re-login).
const SESSION_KEY_PREFIX = 'horus.access.';

// Kedaluwarsa dicek di klien hanya supaya sesi 4 jam berakhir di UI; bukan batas keamanan
// (token palsu lolos). Server tetap memverifikasi tanda tangan dan exp di tiap panggilan API.
const tokenAlive = (tok) => {
  try { return JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp * 1000 > Date.now(); }
  catch { return false; }
};

const SecurityGate = ({ pageName, level = 'root', children }) => {
  const { theme, t, lang } = useApp();
  // Per-tier sessionStorage key — token Profiling tidak grant Datacenter access
  const SESSION_KEY = `${SESSION_KEY_PREFIX}${level}.token`;
  const [granted, setGranted] = useState(() => tokenAlive(sessionStorage.getItem(SESSION_KEY)));
  const [user, setUser]       = useState('');
  const [pass, setPass]       = useState('');
  const [shake, setShake]     = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  // Turnstile hanya di login global (root); login profiling/datacenter terjadi setelah itu, sesi sudah sah.
  // IP robot QA (QA_ALLOW_IPS di server) dibebaskan: widget tidak ditunggu. Hanya kemudahan UI; /api/auth-check
  // tetap memutuskan, jadi flag ini dipalsukan pun tidak membuka apa-apa. Gagal ambil = jalur Turnstile biasa.
  const [qaMode, setQaMode] = useState(false);
  const needTs = level === 'root' && turnstileAktif && !qaMode;
  const [tsToken, setTsToken] = useState('');
  const [tsUlang, setTsUlang] = useState(0);
  const userRef = useRef(null);
  const passRef = useRef(null);

  useEffect(() => {
    if (!granted && userRef.current) userRef.current.focus();
  }, [granted]);

  useEffect(() => {
    if (granted || level !== 'root' || !turnstileAktif) return;
    fetch('/api/qa-mode').then((r) => r.json()).then((d) => setQaMode(d.qa === true)).catch(() => {});
  }, [granted, level]);

  const submit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.trim(), password: pass, level, turnstile_token: tsToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.token) {
        sessionStorage.setItem(SESSION_KEY, data.token);
        // Token data Supabase (role horus_operator) dipasang SEBELUM children dirender, supaya query dan
        // channel realtime pertama sudah membawanya. Tanpa token (belum dikonfigurasi) login HORUS tetap berhasil.
        if (data.supabaseToken) await setDataToken(data.supabaseToken);
        else if (data.supabaseStatus && data.supabaseStatus !== 'not_issued') {
          console.warn('[HORUS] token data Supabase tidak diterbitkan:', data.supabaseStatus);
        }
        setGranted(true);
        setUser(''); setPass('');
        // Keputusan Andra #1057: bangunkan AI tepat setelah login berhasil
        // (login global = level root). Tanpa ditunggu, tanpa keep-alive 24 jam.
        if (level === 'root') bangunkanAI();
      } else {
        setShake(true);
        setError(data.error || `Kredensial salah (${res.status})`);
        setTimeout(() => setShake(false), 400);
        setPass('');
        passRef.current?.focus();
      }
    } catch (err) {
      setShake(true);
      setError('Koneksi gagal: ' + (err?.message || 'unknown'));
      setTimeout(() => setShake(false), 400);
    } finally {
      setLoading(false);
      setTsToken(''); setTsUlang((n) => n + 1); // token Turnstile berlaku sekali: minta yang baru
    }
  };

  const canSubmit = user.trim() && pass && !loading && (!needTs || tsToken);

  if (granted) return children;

  return (
    <div
      className="flex items-center justify-center w-full h-full"
      style={{
        background: 'var(--color-bg)',
        backgroundImage:
          'radial-gradient(circle at 50% 30%, rgba(0,212,255,0.08) 0%, transparent 60%)',
        minHeight: '100%',
      }}
    >
      <div
        className={shake ? 'animate-shake' : ''}
        style={{
          width: 'min(90vw, 420px)',
          padding: '32px 28px',
          background: 'var(--color-card)',
          border: '1px solid rgba(0,212,255,0.25)',
          borderRadius: 16,
          boxShadow: '0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,212,255,0.08)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className="flex items-center justify-center rounded-xl shrink-0"
            style={{
              width: 44, height: 44,
              background: 'rgba(0,212,255,0.12)',
              border: '1px solid rgba(0,212,255,0.3)',
            }}
          >
            <Shield size={22} color="var(--color-cyan)" strokeWidth={2} />
          </div>
          <div>
            <p
              className="text-[10px] font-black tracking-widest font-mono"
              style={{ color: 'var(--color-cyan)' }}
            >
              {lang === 'en' ? 'RESTRICTED ACCESS' : 'AKSES TERBATAS'}
            </p>
            <h2
              className="text-base font-bold"
              style={{ color: 'var(--text-hi)', fontFamily: 'IBM Plex Mono', letterSpacing: '0.02em' }}
            >
              {pageName}
            </h2>
          </div>
        </div>

        {/* Body */}
        <p
          className="text-xs mb-5"
          style={{ color: 'var(--text-mid)', lineHeight: 1.55 }}
        >
          {lang === 'en'
            ? 'This page contains sensitive data. Enter security key to continue.'
            : 'Halaman ini berisi data sensitif. Masukkan kunci keamanan untuk melanjutkan.'}
        </p>

        <form onSubmit={submit}>
          {/* Username */}
          <div className="flex items-center gap-2 mb-2"
            style={{
              padding: '10px 12px',
              background: 'var(--color-bg)',
              border: error ? '1px solid #ff2d55' : '1px solid var(--border-dim)',
              borderRadius: 10,
              transition: 'border-color 200ms',
            }}
          >
            <Shield size={14} color={error ? '#ff2d55' : 'var(--text-dim)'} />
            <input
              ref={userRef}
              type="text"
              id="kes-login-username"
              name="username"
              value={user}
              onChange={e => setUser(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              spellCheck={false}
              className="flex-1 bg-transparent outline-none text-sm"
              style={{
                color: 'var(--text-hi)',
                fontFamily: '"IBM Plex Mono", monospace',
                letterSpacing: '0.05em',
              }}
            />
          </div>
          {/* Password */}
          <div className="flex items-center gap-2 mb-2"
            style={{
              padding: '10px 12px',
              background: 'var(--color-bg)',
              border: error ? '1px solid #ff2d55' : '1px solid var(--border-dim)',
              borderRadius: 10,
              transition: 'border-color 200ms',
            }}
          >
            <Lock size={14} color={error ? '#ff2d55' : 'var(--text-dim)'} />
            <input
              ref={passRef}
              type="password"
              id="kes-login-password"
              name="password"
              value={pass}
              onChange={e => setPass(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              spellCheck={false}
              className="flex-1 bg-transparent outline-none text-sm"
              style={{
                color: 'var(--text-hi)',
                fontFamily: '"IBM Plex Mono", monospace',
                letterSpacing: '0.05em',
              }}
            />
          </div>

          {needTs && <TurnstileWidget key={tsUlang} onToken={setTsToken} />}

          {error && (
            <div className="flex items-center gap-1.5 mb-3">
              <AlertCircle size={12} color="#ff2d55" />
              <p className="text-[10px] font-mono" style={{ color: '#ff2d55' }}>
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full font-bold transition-all duration-200"
            style={{
              padding: '11px 16px',
              marginTop: error ? 0 : 12,
              background: canSubmit ? 'var(--color-cyan)' : 'rgba(0,212,255,0.15)',
              color: canSubmit ? '#02060e' : 'var(--text-dim)',
              border: 'none',
              borderRadius: 10,
              fontSize: 12,
              fontFamily: '"IBM Plex Mono", monospace',
              letterSpacing: '0.1em',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {loading
              ? (lang === 'en' ? 'CHECKING...' : 'MEMERIKSA...')
              : (lang === 'en' ? 'UNLOCK ACCESS' : 'BUKA AKSES')}
          </button>
        </form>

        {/* Footer hint */}
        <p
          className="text-center text-[9px] font-mono mt-5"
          style={{ color: 'var(--text-mute)', letterSpacing: '0.05em' }}
        >
          {lang === 'en' ? 'SESSION-SCOPED · CASE-SENSITIVE' : 'SESI-SCOPED · CASE-SENSITIVE'}
        </p>
      </div>
    </div>
  );
};

export default SecurityGate;
